import React from 'react'
import {
    Button,
    FormControl,
    Popover,
    PopoverTrigger,
    PopoverContent,
    PopoverBody,
    VStack,
    useDisclosure,
    HStack,
    Text,
    Icon,
    Center,
    Divider,
    Portal,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {ChevronDownIcon} from '../../../components/icons'
import {useIntl} from 'react-intl'
import {useHistory} from 'react-router-dom'
import PropTypes from 'prop-types'

const Sort = ({sortingOptions = [], sortUrls, selectedSortingOption, onSelect, ...otherProps}) => {
    const intl = useIntl()
    const history = useHistory()
    const {onOpen, onClose, isOpen} = useDisclosure()
    const selectedLabel = sortingOptions.find((option) => option.id === selectedSortingOption)
        ?.label

    const styles = useMultiStyleConfig('Sort')

    const handleSelect = (href, option) => {
        onClose()
        if (onSelect) {
            onSelect(option)
            return
        }
        history.push(href)
    }

    return (
        <FormControl
            aria-label={intl.formatMessage({
                id: 'product_list.drawer.title.sort_by',
                defaultMessage: 'Sort By'
            })}
            data-testid="sf-product-list-sort"
            id="page_sort"
            sx={styles.container}
            {...otherProps}
        >
            <Popover
                variant="sort"
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={onClose}
                placement="bottom-end"
            >
                <PopoverTrigger data-testid="product-list-sort-popover-trigger">
                    <Button
                        data-testid="plp-sort-trigger"
                        variant="sortSelect"
                        iconSpacing={0}
                        rightIcon={
                            <Icon
                                as={ChevronDownIcon}
                                transform={isOpen ? 'rotate(180deg)' : 'rotate(0deg)'}
                                transition="transform 0.2s"
                            />
                        }
                        sx={styles.triggerButton}
                    >
                        <HStack justify="space-between" gap={2}>
                            <Text sx={styles.label}>
                                {intl.formatMessage({
                                    id: 'product_list.drawer.title.sort_by',
                                    defaultMessage: 'Sort By'
                                })}
                            </Text>
                            {selectedLabel && (
                                <>
                                    <Center sx={styles.dividerWrapper}>
                                        <Divider
                                            orientation="vertical"
                                            sx={styles.divider}
                                        />
                                    </Center>
                                    <Text sx={styles.selectedLabel}>
                                        {selectedLabel}
                                    </Text>
                                </>
                            )}
                        </HStack>
                    </Button>
                </PopoverTrigger>
                <Portal>
                    <PopoverContent sx={styles.popoverContent}>
                        <PopoverBody>
                            <VStack align="stretch" spacing={0}>
                                {sortingOptions.map((option, index) => {
                                    const label = option?.label
                                    const id = option?.id
                                    const href = sortUrls[index]
                                    const isSelected = id === selectedSortingOption

                                    return (
                                        <Button
                                            key={id}
                                            data-testid={`plp-sort-option-${id}`}
                                            variant="sortLink"
                                            onClick={() => handleSelect(href, option)}
                                            borderBottom={
                                                sortUrls.length - 1 === index ? '0' : '1px solid'
                                            }
                                            sx={{
                                                ...styles.optionButton,
                                                ...(isSelected ? styles.selectedOptionButton : {})
                                            }}
                                        >
                                            {label}
                                        </Button>
                                    )
                                })}
                            </VStack>
                        </PopoverBody>
                    </PopoverContent>
                </Portal>
            </Popover>
        </FormControl>
    )
}

Sort.propTypes = {
    sortingOptions: PropTypes.array,
    sortUrls: PropTypes.array.isRequired,
    selectedSortingOption: PropTypes.string,
    onSelect: PropTypes.func
}

export default Sort
